import { useState, useEffect } from 'react';
import { Calendar, Clock, User, MapPin, CheckCircle, XCircle, Edit, MessageCircle } from 'lucide-react';
import { calendarService } from '@/services/calendarService';
import type { Booking } from '@/types';

interface BookingManagerProps {
  providerId: string;
  onBookingUpdate?: (booking: Booking) => void;
  onMessageCustomer?: (customerId: string, bookingId: string) => void;
}

export function BookingManager({ providerId, onBookingUpdate, onMessageCustomer }: BookingManagerProps) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<Booking['status'] | 'ALL'>('ALL');
  const [dateFilter, setDateFilter] = useState<'TODAY' | 'WEEK' | 'MONTH' | 'ALL'>('WEEK');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  useEffect(() => {
    loadBookings();
  }, [providerId]);

  useEffect(() => {
    filterBookings();
  }, [bookings, statusFilter, dateFilter]);

  const loadBookings = async () => {
    setIsLoading(true);
    try {
      const data = await calendarService.getProviderBookings(providerId);
      setBookings(data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    // Filter by status
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Filter by date
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateFilter) {
      case 'TODAY':
        filtered = filtered.filter(booking => {
          const bookingDate = new Date(booking.scheduledDate);
          return bookingDate.toDateString() === today.toDateString();
        });
        break;
      case 'WEEK':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        filtered = filtered.filter(booking => {
          const bookingDate = new Date(booking.scheduledDate);
          return bookingDate >= weekStart && bookingDate <= weekEnd;
        });
        break;
      case 'MONTH':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        filtered = filtered.filter(booking => {
          const bookingDate = new Date(booking.scheduledDate);
          return bookingDate >= monthStart && bookingDate <= monthEnd;
        });
        break;
    }

    // Sort by date and time
    filtered.sort((a, b) => {
      const dateA = new Date(`${a.scheduledDate.toISOString().split('T')[0]}T${a.startTime}`);
      const dateB = new Date(`${b.scheduledDate.toISOString().split('T')[0]}T${b.startTime}`);
      return dateA.getTime() - dateB.getTime();
    });

    setFilteredBookings(filtered);
  };

  const handleStatusUpdate = async (bookingId: string, newStatus: Booking['status']) => {
    try {
      // Mock status update - in real app, call API
      const updatedBookings = bookings.map(booking => 
        booking.id === bookingId 
          ? { ...booking, status: newStatus, updatedAt: new Date() }
          : booking
      );
      setBookings(updatedBookings);
      
      const updatedBooking = updatedBookings.find(b => b.id === bookingId);
      if (updatedBooking) {
        onBookingUpdate?.(updatedBooking);
      }
    } catch (error) {
      console.error('Failed to update booking status:', error);
    }
  };

  const handleCancelBooking = async (bookingId: string, reason: string) => {
    try {
      await calendarService.cancelBooking(bookingId, reason);
      await loadBookings(); // Reload bookings
    } catch (error) {
      console.error('Failed to cancel booking:', error);
    }
  };

  const getStatusColor = (status: Booking['status']) => {
    switch (status) {
      case 'CONFIRMED': return 'text-green-800 bg-green-100 border-green-200';
      case 'PENDING': return 'text-yellow-800 bg-yellow-100 border-yellow-200';
      case 'IN_PROGRESS': return 'text-blue-800 bg-blue-100 border-blue-200';
      case 'COMPLETED': return 'text-purple-800 bg-purple-100 border-purple-200';
      case 'CANCELLED': return 'text-red-800 bg-red-100 border-red-200';
      case 'RESCHEDULED': return 'text-orange-800 bg-orange-100 border-orange-200';
      default: return 'text-gray-800 bg-gray-100 border-gray-200';
    }
  };

  const getStatusActions = (booking: Booking) => {
    const actions = [];

    switch (booking.status) {
      case 'PENDING':
        actions.push(
          <button
            key="confirm"
            onClick={() => handleStatusUpdate(booking.id, 'CONFIRMED')}
            className="btn btn-sm btn-success flex items-center"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Confirm
          </button>
        );
        break;
      case 'CONFIRMED':
        actions.push(
          <button
            key="start"
            onClick={() => handleStatusUpdate(booking.id, 'IN_PROGRESS')}
            className="btn btn-sm btn-primary flex items-center"
          >
            <Clock className="w-4 h-4 mr-1" />
            Start Job
          </button>
        );
        break;
      case 'IN_PROGRESS':
        actions.push(
          <button
            key="complete"
            onClick={() => handleStatusUpdate(booking.id, 'COMPLETED')}
            className="btn btn-sm btn-success flex items-center"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Complete
          </button>
        );
        break;
    }

    if (['PENDING', 'CONFIRMED'].includes(booking.status)) {
      actions.push(
        <button
          key="reschedule"
          onClick={() => {
            setSelectedBooking(booking);
            setShowRescheduleModal(true);
          }}
          className="btn btn-sm btn-outline flex items-center"
        >
          <Edit className="w-4 h-4 mr-1" />
          Reschedule
        </button>
      );

      actions.push(
        <button
          key="cancel"
          onClick={() => {
            const reason = prompt('Reason for cancellation:');
            if (reason) {
              handleCancelBooking(booking.id, reason);
            }
          }}
          className="btn btn-sm btn-error flex items-center"
        >
          <XCircle className="w-4 h-4 mr-1" />
          Cancel
        </button>
      );
    }

    return actions;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-900">Booking Management</h2>
        
        <div className="flex flex-wrap gap-3">
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Booking['status'] | 'ALL')}
            className="input input-sm"
          >
            <option value="ALL">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
            className="input input-sm"
          >
            <option value="TODAY">Today</option>
            <option value="WEEK">This Week</option>
            <option value="MONTH">This Month</option>
            <option value="ALL">All Time</option>
          </select>
        </div>
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.map((booking) => (
          <div key={booking.id} className="card p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Booking Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {booking.serviceType}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(booking.status)}`}>
                    {booking.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-600">
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    {booking.customerName}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    {booking.scheduledDate.toLocaleDateString()}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    {booking.startTime} - {booking.endTime}
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {booking.location}
                  </div>
                </div>

                {booking.notes && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{booking.notes}</p>
                  </div>
                )}

                {booking.estimatedCost && (
                  <div className="mt-2 text-sm font-medium text-green-600">
                    Estimated Cost: €{booking.estimatedCost}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onMessageCustomer?.(booking.customerId, booking.id)}
                  className="btn btn-sm btn-outline flex items-center"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  Message
                </button>

                {getStatusActions(booking)}
              </div>
            </div>
          </div>
        ))}

        {filteredBookings.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
            <p className="text-gray-600">
              {statusFilter !== 'ALL' || dateFilter !== 'ALL'
                ? 'Try adjusting your filters to see more bookings.'
                : 'Your bookings will appear here once customers start booking your services.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedBooking && (
        <RescheduleModal
          booking={selectedBooking}
          onReschedule={async (newDate, newStartTime, newEndTime) => {
            try {
              await calendarService.rescheduleBooking(
                selectedBooking.id,
                newDate,
                newStartTime,
                newEndTime
              );
              await loadBookings();
              setShowRescheduleModal(false);
              setSelectedBooking(null);
            } catch (error) {
              console.error('Failed to reschedule booking:', error);
            }
          }}
          onClose={() => {
            setShowRescheduleModal(false);
            setSelectedBooking(null);
          }}
        />
      )}
    </div>
  );
}

// Reschedule Modal Component
function RescheduleModal({
  booking,
  onReschedule,
  onClose
}: {
  booking: Booking;
  onReschedule: (newDate: string, newStartTime: string, newEndTime: string) => void;
  onClose: () => void;
}) {
  const [newDate, setNewDate] = useState(booking.scheduledDate.toISOString().split('T')[0]);
  const [newStartTime, setNewStartTime] = useState(booking.startTime);
  const [newEndTime, setNewEndTime] = useState(booking.endTime);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onReschedule(newDate, newStartTime, newEndTime);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Reschedule Booking
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Date
            </label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="input w-full"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="time"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="time"
                value={newEndTime}
                onChange={(e) => setNewEndTime(e.target.value)}
                className="input w-full"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              Reschedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}